import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Crown, Check, Clock, Users, DollarSign, FileText, Video, Music, Image, Link, File, Upload } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import FileUploader from '../../components/FileUploader';

const FILE_TYPES = [
  { value: 'url', label: 'URL/Link', icon: Link },
  { value: 'mp4', label: 'Video (MP4)', icon: Video },
  { value: 'mp3', label: 'Audio (MP3)', icon: Music },
  { value: 'pdf', label: 'PDF Document', icon: FileText },
  { value: 'doc', label: 'Word Document', icon: File },
  { value: 'image', label: 'Image', icon: Image },
];

const AdminActivationPacks = () => {
  const [packs, setPacks] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: 990,
    duration_days: 365,
    description: '',
    features: [],
    files: [],
    is_active: true,
    is_featured: false,
    order: 0
  });
  const [newFeature, setNewFeature] = useState('');
  const [newFile, setNewFile] = useState({ name: '', url: '', file_type: 'url' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packsRes, requestsRes, statsRes] = await Promise.all([
        api.get('/admin/activation/packs'),
        api.get('/admin/activation/requests'),
        api.get('/admin/activation/stats')
      ]);
      setPacks(packsRes.data);
      setPendingRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.name.trim()) {
      toast.error('Pack name is required');
      return;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    if (!formData.duration_days || formData.duration_days <= 0) {
      toast.error('Duration must be greater than 0');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        duration_days: parseInt(formData.duration_days),
        description: formData.description || '',
        features: formData.features || [],
        files: formData.files || [],
        is_active: formData.is_active !== false,
        is_featured: formData.is_featured || false,
        order: parseInt(formData.order) || 0
      };
      
      if (editingPack) {
        await api.put(`/admin/activation/packs/${editingPack.id}`, payload);
        toast.success('Pack updated successfully');
      } else {
        await api.post('/admin/activation/packs', payload);
        toast.success('Pack created successfully');
      }
      setShowForm(false);
      setEditingPack(null);
      resetForm();
      await fetchData();
    } catch (error) {
      console.error('Pack save error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to save pack';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (pack) => {
    setEditingPack(pack);
    setFormData({
      name: pack.name,
      price: pack.price,
      duration_days: pack.duration_days,
      description: pack.description,
      features: pack.features || [],
      files: pack.files || [],
      is_active: pack.is_active,
      is_featured: pack.is_featured,
      order: pack.order
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pack?')) return;
    try {
      await api.delete(`/admin/activation/packs/${id}`);
      toast.success('Pack deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pack');
    }
  };

  const handleApprove = async (activationId) => {
    try {
      await api.post(`/admin/activation/approve/${activationId}`);
      toast.success('Activation approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (activationId) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      await api.post(`/admin/activation/reject/${activationId}`, null, {
        params: { reason }
      });
      toast.success('Activation rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: 990,
      duration_days: 365,
      description: '',
      features: [],
      files: [],
      is_active: true,
      is_featured: false,
      order: 0
    });
    setNewFeature('');
    setNewFile({ name: '', url: '', file_type: 'url' });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const addFile = () => {
    if (newFile.name.trim() && newFile.url.trim()) {
      setFormData({
        ...formData,
        files: [...formData.files, { ...newFile, name: newFile.name.trim(), url: newFile.url.trim() }]
      });
      setNewFile({ name: '', url: '', file_type: 'url' });
    }
  };

  const removeFile = (index) => {
    setFormData({
      ...formData,
      files: formData.files.filter((_, i) => i !== index)
    });
  };

  const getFileIcon = (fileType) => {
    const type = FILE_TYPES.find(t => t.value === fileType);
    if (type) {
      const Icon = type.icon;
      return <Icon className="w-4 h-4" />;
    }
    return <File className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-activation-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Activation Packs</h1>
            <p className="text-muted-foreground">Manage Affiliate Network activation packages</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingPack(null); resetForm(); }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-pack-btn"
          >
            <Plus className="w-5 h-5" />
            Create Pack
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="glass-card p-4 text-center">
              <Users className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-500">{stats.total_active}</p>
              <p className="text-sm text-muted-foreground">Active Users</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Crown className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-500">{stats.expired}</p>
              <p className="text-sm text-muted-foreground">Expired</p>
            </div>
            <div className="glass-card p-4 text-center">
              <DollarSign className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-primary">₹{stats.total_revenue}</p>
              <p className="text-sm text-muted-foreground">Revenue</p>
            </div>
          </div>
        )}

        {/* Pending Activation Requests */}
        {pendingRequests.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-yellow-500" />
              Pending Approvals ({pendingRequests.length})
            </h2>
            <div className="space-y-4">
              {pendingRequests.map((req) => (
                <div key={req.id} className="glass-card p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{req.user?.name || 'Unknown'}</p>
                    <p className="text-sm text-muted-foreground">{req.user?.email}</p>
                    <p className="text-sm">Pack: {req.pack_name} • ₹{req.amount_paid}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(req.id)}
                      className="px-4 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(req.id)}
                      className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pack Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
            <div className="glass-card p-8 max-w-2xl w-full my-8">
              <h2 className="text-2xl font-bold mb-6">
                {editingPack ? 'Edit Pack' : 'Create New Pack'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Pack Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                      placeholder="Freedom Pack"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Price (₹) *</label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration (Days) *</label>
                    <input
                      type="number"
                      value={formData.duration_days}
                      onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Pack description..."
                  />
                </div>

                {/* Features */}
                <div>
                  <label className="block text-sm font-medium mb-2">Features</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      placeholder="Add feature..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <button
                      type="button"
                      onClick={addFeature}
                      className="px-4 py-2 bg-primary/20 text-primary rounded-xl"
                    >
                      Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg">
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="flex-1">{feature}</span>
                        <button
                          type="button"
                          onClick={() => removeFeature(idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Files Section - Device Upload */}
                <div className="border border-primary/30 rounded-xl p-4 bg-primary/5">
                  <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-primary" />
                    Pack Files (Upload from Device)
                  </label>
                  <FileUploader
                    contentType="pack"
                    existingFiles={formData.files || []}
                    onFileUploaded={(file) => {
                      setFormData({
                        ...formData,
                        files: [...(formData.files || []), file]
                      });
                    }}
                    onFileRemoved={(idx) => {
                      setFormData({
                        ...formData,
                        files: (formData.files || []).filter((_, i) => i !== idx)
                      });
                    }}
                  />
                  
                  {/* Manual URL Entry (Alternative) */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-xs text-muted-foreground mb-2">Or add files via URL:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={newFile.name}
                        onChange={(e) => setNewFile({ ...newFile, name: e.target.value })}
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                        placeholder="File name"
                      />
                      <input
                        type="url"
                        value={newFile.url}
                        onChange={(e) => setNewFile({ ...newFile, url: e.target.value })}
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                        placeholder="File URL"
                      />
                      <div className="flex gap-2">
                        <select
                          value={newFile.file_type}
                          onChange={(e) => setNewFile({ ...newFile, file_type: e.target.value })}
                          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                        >
                          {FILE_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={addFile}
                          className="px-4 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <span>Featured</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="flex-1 btn-primary disabled:opacity-50"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : (editingPack ? 'Update Pack' : 'Create Pack')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingPack(null); resetForm(); }}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Packs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div className="grid gap-4">
            <h2 className="text-2xl font-bold">All Packs</h2>
            {packs.map((pack) => (
              <div
                key={pack.id}
                className={`glass-card p-6 border ${pack.is_active ? 'border-primary/30' : 'border-white/10 opacity-60'}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-6 h-6 text-primary" />
                      <h3 className="text-xl font-bold">{pack.name}</h3>
                      {pack.is_featured && (
                        <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">Featured</span>
                      )}
                      {!pack.is_active && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-500 text-xs rounded-full">Inactive</span>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-2">{pack.description}</p>
                    <p className="text-2xl font-bold text-primary">₹{pack.price} <span className="text-sm text-muted-foreground font-normal">/ {pack.duration_days} days</span></p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(pack)}
                      className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(pack.id)}
                      className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivationPacks;
